import React from 'react';
import clsx from 'clsx';
import { ScheduleItem, ScheduleItemContract, TableColumn } from '@app-types';
import styles from './ScheduleTableBody.module.css';

export interface RenderCellParams {
  column: TableColumn;
  item: ScheduleItem;
  contract: ScheduleItemContract;
  isGroupRow: boolean;
  isChildRow: boolean;
  isSelected?: boolean;
  isGroupExpanded?: boolean;
  contractsInGroup?: ScheduleItemContract[];
}

type RenderCellFunction = (params: RenderCellParams) => React.ReactElement;

interface ScheduleTableBodyProps {
  scheduleItems: ScheduleItem[];
  columns: TableColumn[];
  isExpanded: (itemId: number) => boolean;
  renderCell: RenderCellFunction;
  // Optional props for selection (Scheduler-specific)
  selectedItems?: Set<number>;
  onToggleSelection?: (itemId: number | null, contractId: number, item: ScheduleItem) => void;
  isItemSelected?: (item: ScheduleItem) => boolean;
}

const ScheduleTableBody: React.FC<ScheduleTableBodyProps> = ({
  scheduleItems,
  columns,
  isExpanded,
  renderCell,
  selectedItems,
  onToggleSelection,
  isItemSelected
}) => {
  const hasSelection = !!selectedItems && !!onToggleSelection && !!isItemSelected;

  // Check if item has open conflicts
  const hasOpenConflicts = (contract: ScheduleItemContract) => {
    return (contract.conflicts || []).some((c) => c.status !== 'resolved');
  };

  return (
    <tbody>
      {scheduleItems.map((item) => {
        const isGroup = item.contracts.length > 1;
        const isGroupExpanded = item.id !== null && isExpanded(item.id);
        const isSelected = hasSelection && isItemSelected ? isItemSelected(item) : false;
        const firstContract = item.contracts[0];

        // Check if any contract in the group has conflicts
        const hasConflicts = item.contracts.some(hasOpenConflicts);

        return (
          <React.Fragment key={`item-${item.id}-${firstContract.id}`}>
            {/* Parent/Main Row - for both single and group */}
            <tr
              className={clsx(
                styles.tableRow,
                isGroup ? styles.groupRow : styles.singleRow,
                isSelected && styles.selected,
                hasConflicts && styles.hasConflict
              )}
              onClick={
                hasSelection && !isGroup && onToggleSelection
                  ? () => onToggleSelection(item.id, firstContract.id, item)
                  : undefined
              }
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  onClick={
                    hasSelection
                      ? (e) => {
                          // Prevent row selection when clicking interactive elements
                          if ((e.target as HTMLElement).closest('button, input, a')) {
                            e.stopPropagation();
                          }
                        }
                      : undefined
                  }
                >
                  {renderCell({
                    column,
                    item,
                    contract: firstContract,
                    isGroupRow: isGroup,
                    isChildRow: false,
                    isSelected,
                    isGroupExpanded,
                    contractsInGroup: item.contracts
                  })}
                </td>
              ))}
            </tr>

            {/* Child rows (when group is expanded) */}
            {isGroup &&
              isGroupExpanded &&
              item.contracts.map((contract) => (
                <tr
                  key={`${item.id}-${contract.id}`}
                  className={clsx(styles.tableRow, styles.childRow, hasOpenConflicts(contract) && styles.hasConflict)}
                >
                  {columns.map((column) => (
                    <td key={column.key}>
                      {renderCell({
                        column,
                        item,
                        contract,
                        isGroupRow: true,
                        isChildRow: true,
                        isSelected: false,
                        isGroupExpanded: false,
                        contractsInGroup: item.contracts
                      })}
                    </td>
                  ))}
                </tr>
              ))}
          </React.Fragment>
        );
      })}
    </tbody>
  );
};

export default ScheduleTableBody;
